import { useState, useEffect, useCallback } from 'react';
import { ASSETS, CONFIG } from '../data/assets';

const TODAY = new Date().toISOString().split('T')[0];

// Storage keys scoped to this lease so data never bleeds between requests
const leaseKey  = CONFIG.lease.replace(/-/g, '_');
const SAVE_KEY  = `prc_hrs_${leaseKey}`;
const LOG_KEY   = `prc_log_${leaseKey}`;
const META_KEY  = `prc_meta_${leaseKey}`;

function readJSON(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; }
  catch { return fallback; }
}

function writeJSON(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage full */ }
}

// ─── Build initial row state from saved data ───────────────────
function buildInitialRows() {
  const saved = readJSON(SAVE_KEY, {});
  return ASSETS.map((a) => {
    const s = saved[a.id] || {};
    const defaultNA = a.category !== 'MHE';
    return {
      id:    a.id,
      na:    s.na !== undefined ? s.na : defaultNA,
      hours: s.hours || '',
      date:  s.date  || TODAY,
    };
  });
}

// ─── Main hook ────────────────────────────────────────────────
export function useHoursForm() {
  const [rows,      setRows]      = useState(buildInitialRows);
  const [log,       setLog]       = useState(() => readJSON(LOG_KEY,  []));
  const [meta,      setMeta]      = useState(() => readJSON(META_KEY, {}));
  const [submitted, setSubmitted] = useState(false);
  const [toast,     setToast]     = useState(null); // { msg, type }

  // ── Record page open on mount ──
  useEffect(() => {
    const newMeta = {
      ...readJSON(META_KEY, {}),
      opens:     (readJSON(META_KEY, {}).opens || 0) + 1,
      firstOpen: readJSON(META_KEY, {}).firstOpen || new Date().toISOString(),
    };
    writeJSON(META_KEY, newMeta);
    setMeta(newMeta);
    addLogEvent('open', 'Form opened');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Toast auto-dismiss ──
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  function showToast(msg, type = '') {
    setToast({ msg, type });
  }

  function addLogEvent(type, detail) {
    const entry = { type, detail, time: new Date().toISOString() };
    setLog(prev => {
      const next = [entry, ...prev].slice(0, 100);
      writeJSON(LOG_KEY, next);
      return next;
    });
  }

  // ── Row field updates ──
  const updateRow = useCallback((id, field, value) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  }, []);

  function setHours(id, raw) {
    const val = raw.replace(/\D/g, '');
    updateRow(id, 'hours', val);
  }

  function setDate(id, val) {
    if (val > TODAY) {
      showToast('📅 Future dates are not allowed — reset to today.', 'warning');
      updateRow(id, 'date', TODAY);
    } else {
      updateRow(id, 'date', val);
    }
  }

  function toggleNA(id) {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      const isNA = !r.na;
      return {
        ...r,
        na:    isNA,
        hours: isNA ? '' : r.hours,
        date:  isNA ? r.date : TODAY,
      };
    }));
  }

  // ── Progress calculation ──
  const progress = rows.reduce((acc, r) => {
    const isMHE = ASSETS[r.id].category === 'MHE';
    const done  = r.na || (r.hours !== '' && r.date !== '' && r.date <= TODAY);
    return {
      done:      acc.done + (done ? 1 : 0),
      total:     acc.total + 1,
      mheDone:   acc.mheDone   + (isMHE && done && !r.na ? 1 : 0),
      mheTotal:  acc.mheTotal  + (isMHE ? 1 : 0),
      otherTotal: acc.otherTotal + (!isMHE ? 1 : 0),
      pending:   acc.pending + (!done ? 1 : 0),
    };
  }, { done: 0, total: 0, mheDone: 0, mheTotal: 0, otherTotal: 0, pending: 0 });

  const allDone = progress.done === progress.total;

  // ── Save partial ──
  function savePartial() {
    const data = {};
    rows.forEach(r => { data[r.id] = { na: r.na, hours: r.hours, date: r.date }; });
    writeJSON(SAVE_KEY, data);

    const filled = rows.filter(r => r.na || r.hours).length;
    const newMeta = { ...meta, partials: (meta.partials || 0) + 1 };
    writeJSON(META_KEY, newMeta);
    setMeta(newMeta);
    addLogEvent('partial', `Progress saved — ${filled} of ${rows.length} assets filled`);
    showToast(`✓ Progress saved! ${filled} of ${rows.length} assets sent to PRC.`, 'success');
  }

  // ── Full submit ──
  function submit() {
    savePartial();
    const newMeta = { ...meta, submits: (meta.submits || 0) + 1 };
    writeJSON(META_KEY, newMeta);
    setMeta(newMeta);
    addLogEvent('submit', `Full submission — all ${rows.length} assets reported`);
    setSubmitted(true);
  }

  // ── Tracker actions ──
  function resetTracker() {
    if (!window.confirm('Reset all tracker data? Cannot be undone.')) return;
    writeJSON(LOG_KEY,  []);
    writeJSON(META_KEY, {});
    setLog([]);
    setMeta({});
    showToast('Tracker data reset.', '');
  }

  function clearLog() {
    if (!window.confirm('Clear activity log?')) return;
    writeJSON(LOG_KEY, []);
    setLog([]);
  }

  return {
    rows, progress, allDone, submitted, toast,
    log, meta,
    setHours, setDate, toggleNA,
    savePartial, submit,
    resetTracker, clearLog,
  };
}
